import React, { useMemo, useState } from 'react';
import { signInAnonymously, signOut } from 'firebase/auth';
import { ASSETS, LAW_FIRM_INFO } from '../../config/Config';
import { auth } from '../../shared/api/firebase/config';
import { submitIntakeLead } from '../../services/IntakeFormRepository';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useDriveUpload } from '../../hooks/useDriveUpload';

export default function IntakeForm() {
  const { user: currentUser } = useCurrentUser();
  const { uploadFile: uploadToDrive } = useDriveUpload();
  const [fullName, setFullName] = useState('');
  const [whatsApp, setWhatsApp] = useState('');
  const [brief, setBrief] = useState('');
  const [files, setFiles] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);

  const brandTitle = useMemo(() => {
    const fallback = 'Rodrigo Demeterco Advocacia';
    const name = String(LAW_FIRM_INFO?.brand?.name || LAW_FIRM_INFO?.name || '').trim();
    return name || fallback;
  }, []);

  const safeFileName = (name) => String(name || 'arquivo').replace(/[^a-zA-Z0-9._-]/g, '_');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    setError('');

    const cleanName = String(fullName || '').trim();
    const cleanWhatsapp = String(whatsApp || '').trim();
    const cleanBrief = String(brief || '').trim();

    if (!cleanName || !cleanWhatsapp || !cleanBrief) {
      setError('Por favor, preencha Nome Completo, WhatsApp e Relato Breve do Caso.');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(null);

    try {
      let authUser = currentUser;
      if (!authUser) {
        const cred = await signInAnonymously(auth);
        authUser = cred.user;
      }

      const uid = (authUser?.uid) || 'anonymous';
      const now = Date.now();

      const attachments = [];
      const totalFiles = Array.isArray(files) ? files.length : 0;

      for (let i = 0; i < totalFiles; i += 1) {
        const file = files[i];
        if (!file) continue;

        const safeName = safeFileName(file.name);
        const legacyObjectPath = `gdrive://intake_uploads/${now}_${uid}_${i + 1}_${safeName}`;

        setUploadProgress({
          state: 'preparing_drive_session',
          current: i + 1,
          total: totalFiles,
          percent: 10,
          fileName: safeName,
        });

        const uploadResult = await uploadToDrive({
          file,
          fileName: `${now}_${uid}_${i + 1}_${safeName}`,
        });
        const storageEngine = uploadResult?.storageEngineData && typeof uploadResult.storageEngineData === 'object'
          ? uploadResult.storageEngineData
          : null;
        const url = String(storageEngine?.webViewLink || storageEngine?.webContentLink || '').trim();
        if (!url) {
          throw new Error('Google Drive não retornou URL pública para o anexo do intake.');
        }

        setUploadProgress({
          state: 'uploaded_to_drive',
          current: i + 1,
          total: totalFiles,
          percent: 100,
          fileName: safeName,
        });

        attachments.push({
          name: safeName,
          original_name: String(file.name || safeName),
          size: file.size || null,
          type: file.type || null,
          storage_path: legacyObjectPath,
          storageEngine,
          url,
        });
      }

      await submitIntakeLead({
        fullName: cleanName,
        whatsapp: cleanWhatsapp,
        brief: cleanBrief,
        attachments,
        uid,
        isAnonymous: Boolean(authUser?.isAnonymous),
      });

      setSuccess(true);

      try {
        if (authUser?.isAnonymous) {
          await signOut(auth);
        }
      } catch (e2) {
      }
    } catch (err) {
      const msg = String(err?.message || err);
      setError(msg);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="w-full max-w-lg rounded-2xl border border-emerald-200 bg-white shadow-md p-8 text-center">
          <div className="text-sm font-extrabold uppercase tracking-widest text-emerald-700">Recebido</div>
          <div className="mt-3 text-sm text-gray-700">
            Recebemos o seu caso. Nossa equipe jurídica analisará os documentos e entrará em contato.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        <div className="mb-4 text-center">
          {ASSETS?.logoIcon ? (
            <img
              src={ASSETS.logoIcon}
              alt={brandTitle}
              className="mx-auto h-12 w-12 object-contain"
            />
          ) : null}
          <div className="mt-3 text-sm font-extrabold uppercase tracking-widest text-[#20243B]">{brandTitle}</div>
          <div className="mt-2 text-xs text-gray-600">Formulário de Triagem Inicial (sem necessidade de senha)</div>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-white shadow-md p-5 sm:p-8">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-widest text-gray-600">Nome Completo</label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#20243B]"
                placeholder="Seu nome completo"
                autoComplete="name"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-widest text-gray-600">WhatsApp</label>
              <input
                value={whatsApp}
                onChange={(e) => setWhatsApp(e.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#20243B]"
                placeholder="(00) 00000-0000"
                autoComplete="tel"
                inputMode="tel"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-widest text-gray-600">Relato Breve do Caso</label>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                className="mt-2 w-full min-h-[140px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#20243B]"
                placeholder="Conte resumidamente o que aconteceu, datas, local, e o que você precisa."
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-widest text-gray-600">Anexos (Documentos / Fotos)</label>
              <input
                type="file"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="mt-2 block w-full text-sm"
                disabled={isSubmitting}
              />
              {files?.length ? (
                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-gray-700">
                  <div className="font-bold text-gray-700">Arquivos selecionados:</div>
                  <div className="mt-2 space-y-1">
                    {files.map((f, idx) => (
                      <div key={`${f?.name || 'file'}_${idx}`} className="truncate">
                        {f?.name || 'Arquivo'}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 whitespace-pre-wrap">{error}</div>
            ) : null}

            {uploadProgress ? (
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-[11px] font-extrabold uppercase tracking-widest text-gray-600">Upload</div>
                <div className="mt-2 text-xs text-gray-700 truncate">{uploadProgress.fileName}</div>
                <div className="mt-2 h-2 w-full rounded bg-slate-100 overflow-hidden">
                  <div
                    className="h-2 bg-[#20243B]"
                    style={{ width: `${Math.max(0, Math.min(100, uploadProgress.percent || 0))}%` }}
                  />
                </div>
                <div className="mt-2 text-[11px] text-gray-600">
                  Arquivo {uploadProgress.current} de {uploadProgress.total} — {uploadProgress.percent || 0}%
                </div>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-2 w-full rounded-xl bg-[#20243B] px-4 py-3 text-sm font-extrabold uppercase tracking-widest text-white disabled:opacity-60"
            >
              {isSubmitting ? 'Enviando...' : 'Enviar para Triagem'}
            </button>

            <div className="text-[11px] text-gray-500 text-center">
              Ao enviar, você autoriza o recebimento e análise dos documentos exclusivamente para triagem.
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
